class BlakeMillScrollingContent extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.bm-scrolling__track');
    this.group = this.querySelector('.bm-scrolling__group');
    this.viewport = this.querySelector('.bm-scrolling__viewport');
    this.button = this.querySelector('.bm-scrolling__pause');
    this.repeats ??= 1;
    this.measurements = null;
    this.events = new AbortController();
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.button.addEventListener('click', () => {
      const paused = this.toggleAttribute('data-paused');
      this.button.setAttribute('aria-pressed', String(paused));
      this.button.setAttribute('aria-label', paused ? 'Resume scrolling' : 'Pause scrolling');
      this.button.firstElementChild.textContent = paused ? '▶' : 'Ⅱ';
    }, { signal: this.events.signal });
    this.addEventListener('shopify:block:select', () => this.setAttribute('data-paused', ''), { signal: this.events.signal });
    this.addEventListener('shopify:block:deselect', () => {
      if (this.button.getAttribute('aria-pressed') !== 'true') this.removeAttribute('data-paused');
    }, { signal: this.events.signal });
    this.motion.addEventListener('change', () => this.schedule(), { signal: this.events.signal });
    this.observer = new ResizeObserver(() => this.schedule());
    this.observer.observe(this.viewport);
    this.observer.observe(this.group);
    // Font swaps change the width of one complete repeat.
    const { signal } = this.events;
    document.fonts.ready.then(() => { if (!signal.aborted) this.schedule(); });
    this.schedule();
  }

  schedule() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.build());
  }

  copy(element) {
    const copy = element.cloneNode(true);
    copy.dataset.copy = '';
    copy.setAttribute('aria-hidden', 'true');
    for (const node of [copy, ...copy.querySelectorAll('*')]) {
      node.removeAttribute('id');
      node.removeAttribute('data-shopify-editor-block');
      if (node.matches('a, button')) node.setAttribute('tabindex', '-1');
    }
    return copy;
  }

  build() {
    if (!this.isConnected) return;
    const originals = [...this.group.children].filter((item) => !item.hasAttribute('data-copy'));
    if (this.motion.matches || !originals.length) {
      this.querySelectorAll('[data-copy]').forEach((copy) => copy.remove());
      this.removeAttribute('data-ready');
      this.button.hidden = true;
      this.repeats = 1;
      this.measurements = null;
      return;
    }

    // Each repeat includes its trailing gap. Measure the existing group before
    // any writes, then derive both the original and final widths arithmetically.
    const width = this.group.getBoundingClientRect().width / this.repeats;
    const viewportWidth = this.viewport.clientWidth;
    if (!width || !viewportWidth) return;
    const count = Math.max(1, Math.ceil(viewportWidth / width));
    const speed = Math.max(0.1, Number(this.dataset.speed) || 0.5) * 100;
    if (this.measurements && Math.abs(this.measurements.width - width) < 0.01 &&
        this.measurements.count === count && this.measurements.speed === speed) return;

    const copies = document.createDocumentFragment();
    for (let i = 1; i < count; i++) {
      for (const item of originals) copies.append(this.copy(item));
    }
    this.querySelectorAll('[data-copy]').forEach((copy) => copy.remove());
    this.group.append(copies);
    this.track.append(this.copy(this.group));
    this.repeats = count;
    this.measurements = { width, count, speed };
    this.button.hidden = false;
    this.style.setProperty('--scroll-duration', `${width * count / speed}s`);
    this.setAttribute('data-ready', '');
  }

  disconnectedCallback() {
    this.events.abort();
    this.observer.disconnect();
    cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('bm-scrolling-content')) {
  customElements.define('bm-scrolling-content', BlakeMillScrollingContent);
}
